import { coverEncodeLadder, COVER_DOWNLOAD_MAX_BYTES, needsCoverReencode } from '@/lib/cover/coverDownload';
import { withStillCorsCacheKey } from '@/lib/cover/withStillCorsCacheKey';

function canvasToBlob(canvas: HTMLCanvasElement, quality: number): Promise<Blob | null> {
  return new Promise((resolve) => canvas.toBlob(resolve, 'image/jpeg', quality));
}

async function reencode(blob: Blob): Promise<Blob | null> {
  const bitmap = await createImageBitmap(blob);
  try {
    let last: Blob | null = null;
    for (const attempt of coverEncodeLadder({ width: bitmap.width, height: bitmap.height })) {
      const canvas = document.createElement('canvas');
      canvas.width = attempt.width;
      canvas.height = attempt.height;
      const ctx = canvas.getContext('2d');
      if (!ctx) return null;
      ctx.fillStyle = '#fff';
      ctx.fillRect(0, 0, attempt.width, attempt.height);
      ctx.drawImage(bitmap, 0, 0, attempt.width, attempt.height);
      const out = await canvasToBlob(canvas, attempt.quality);
      if (!out) return null;
      last = out;
      if (out.size <= COVER_DOWNLOAD_MAX_BYTES) break;
    }
    return last;
  } finally {
    bitmap.close?.();
  }
}

export async function downloadCoverImage({
  url,
  fileName,
}: {
  url: string;
  fileName: string;
}): Promise<{ ok: boolean }> {
  let objectUrl: string | null = null;
  try {
    const response = await fetch(withStillCorsCacheKey(url), { mode: 'cors' });
    if (!response.ok) return { ok: false };
    let blob = await response.blob();
    if (needsCoverReencode(blob)) {
      const encoded = await reencode(blob);
      if (!encoded) return { ok: false };
      blob = encoded;
    }
    objectUrl = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = objectUrl;
    a.download = fileName;
    document.body.appendChild(a);
    a.click();
    a.remove();
    // Safari can fail the download if the URL is revoked right after click.
    const toRevoke = objectUrl;
    setTimeout(() => URL.revokeObjectURL(toRevoke), 1000);
    return { ok: true };
  } catch {
    if (objectUrl) URL.revokeObjectURL(objectUrl);
    return { ok: false };
  }
}
